import localizedFormat from 'dayjs/plugin/localizedFormat';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import dayjs from '../../dayjs/dayjs_timezone';
import { parseCartItems } from './cartUtility';
import Translation from './localizationUtility';

dayjs.extend(localizedFormat);
dayjs.extend(advancedFormat);

const defaultTranslation = new Translation({
  translation: {
    cutoffConflictMessage: 'date for accepting bookings is cut off',
    blackoutConflictMessage: 'there is a blackout in effect',
    openConflictMessage: 'the shop is not open at this time',
    scheduleConflictMessage: 'it is not scheduled at this time',
    bookingConflictMessage: 'item is fully booked at the requested time',
    resourcesConflictMessage: 'resource is already booked',
    pastDateConflictMessage: "can't be in the past",
  },
});

const translationByKey = (t, key) => t.key(key) || defaultTranslation.key(key);

export const parseStatus = (string, t = defaultTranslation) => {
  switch (string) {
    case 'ok':
      return 'success';
    case 'cutoff_conflict':
      return translationByKey(t, 'cutoffConflictMessage');
    case 'blackout_conflict':
      return translationByKey(t, 'blackoutConflictMessage');
    case 'open_conflict':
      return translationByKey(t, 'openConflictMessage');
    case 'schedule_conflict':
      return translationByKey(t, 'scheduleConflictMessage');
    case 'booking_conflict':
      return translationByKey(t, 'bookingConflictMessage');
    case 'resources_conflict':
      return translationByKey(t, 'resourcesConflictMessage');
    default:
      const past = string ? string.match(/can't be in the past/) : 'Error';
      return past ? translationByKey(t, 'pastDateConflictMessage') : string;
  }
};

export const parseMessageTime = (message) => {
  const utcDates = message.match(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z/g);
  utcDates
    && utcDates.forEach((date) => {
      message = message.replace(date, dayjs(date).tz(window.bta.timeZone).format('LLL (z)'));
    });
  return message;
};

const mergeItems = (items, anotherItems) => items.reduce((all, item) => {
  const anItems = anotherItems.find((x) => x.external_id === item.external_id);
  all.push({ ...item, ...anItems });
  return all;
}, []);

export const parseErrors = (_cartItems, _error) => {
  const { items: errorMessages, variants: extraData, errors } = _error.data;

  const cartItems = mergeItems(parseCartItems(_cartItems), extraData);

  if (errorMessages) {
    // on_invalid
    errorMessages.forEach((errorMessage) => {
      const externalId = +errorMessage.match(/\[(\d+)\]/)[1];
      const startStr = errorMessage.match(/[^\[]+(?=\])/g)[1];
      const start = dayjs(startStr).format() === 'Invalid date' ? undefined : dayjs(startStr).tz(window.bta.timeZone);
      const message = errorMessage.match(/items\[\d+\]\[[^\[]+\]\[[^\[]+\]: \[\"(.+)\"\]/)[1];

      let item = cartItems.find((x) => {
        const isSameStart = start ? x.start === start.clone().format().substr(0, 16) || x.start === start.clone().format('YYYY-MM-DD') : !x.start;
        return x.external_id === externalId && isSameStart;
      });

      if (!item) item = cartItems.find((x) => x.external_id === externalId);

      item.status = parseMessageTime(message, window.bta.timeZone);
    });
  } else {
    // on_failed
    errors.forEach((error) => {
      const item = cartItems.find((i) => i.external_id === error.external_id);
      item.status = error.messages.length ? error.messages.join(', ') : 'Error';
    });
  }
  return cartItems;
};

export const parseResponseErrors = (_items, response, translation = defaultTranslation) => {
  const items = Object.values(_items).map((item) => ({
    variant_id: item.external_id || item.externalId,
    quantity: item.quantity,
  }));
  return [...new Set(parseErrors(items, response).map((item) => parseStatus(item.status, translation)))].filter((e) => e !== 'success');
};

export const parseBookingResponseError = (messages, translation, defaultMessage) => {
  if (messages.email || messages['customer.email']) {
    return `${translation.key('customerEmail')} ${translation.key('invalidFormatMessage')}`;
  }

  if (messages.phone || messages['customer.phone']) {
    return `${translation.key('customerPhone')} ${translation.key('invalidFormatMessage')}`;
  }

  if (messages.booking_limit_reached) {
    return messages.booking_limit_reached
  }

  const error = messages.variant ? messages.variant[0] : null;

  if (error) {
    const regex = /\((.*?)\)/gi;
    const resultMatchGroup = error.match(regex);
    if (!resultMatchGroup) return defaultMessage;
    const desiredRes = resultMatchGroup.map((match) => match.replace(regex, '$1'));
    return parseStatus(desiredRes[0], translation);
  }
  return defaultMessage;
};
